import styled from '@emotion/styled'
import Row from 'react-bootstrap/Row'
import Card from 'react-bootstrap/Card'
import { Form } from 'react-bootstrap'
import theme from '../../constants/theme'
import { Typography } from '../styled'
import PrimaryButton from '../buttons/PrimaryButton'

/* ---------- generic ---------- */

export const LoadingWrap = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
})

export const StickyHeader = styled(Row)({
  position: 'sticky',
  top: 0,
  zIndex: 1000,
  backgroundColor: theme.white,
  padding: '0px 20px',
})

export const NoWrapText = styled(Typography)({
  whiteSpace: 'nowrap',
})

/* ---------- intro screen ---------- */

export const IntroCenter = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  minHeight: '100%',
})

export const AssessmentCard = styled(Card)({
  borderRadius: '20px',
  padding: '36px',
  position: 'relative',
  overflow: 'hidden',
})

export const DecorCircle = styled('div')(({ size, offset }) => ({
  position: 'absolute',
  top: -offset,
  right: -offset,
  width: size,
  height: size,
  borderRadius: '50%',
  border: '1px solid #EDF1F5',
}))

export const CardInner = styled('div')({
  position: 'relative',
})

export const TypeBadge = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: '#F1F5FA',
  color: theme.brandColorOrange,
  fontSize: '12px',
  fontWeight: 600,
  padding: '5px 12px',
  borderRadius: '20px',
})

export const IntroDescription = styled('div')({
  fontSize: 14,
  color: theme.talentinoSecondaryGray,
})

export const StatBox = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '14px 16px',
  borderRadius: '12px',
  background: theme.talentinoLightBackground,
  border: '1px solid #EDF1F5',
})

export const StatIcon = styled('span')({
  width: 38,
  height: 38,
  borderRadius: '10px',
  background: '#F1F5FA',
  color: theme.brandColorOrange,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
})

export const StartButton = styled(PrimaryButton)({
  borderRadius: '12px',
  fontSize: '15px',
  gap: '8px',
})

/* ---------- active screen ---------- */

export const TimerPill = styled('div')(({ danger }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  background: danger ? '#FFF0F0' : '#F1F5FA',
  padding: '8px',
  borderRadius: '20px',
  border: `1px solid ${danger ? '#FFC9C9' : '#CCE5FF'}`,
  color: danger ? theme.talentinoRed : theme.talentinoBlue,
}))

export const TimerText = styled('span')({
  fontSize: '14px',
  fontWeight: 'bold',
})

export const ScrollArea = styled('div')({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  scrollbarWidth: 'thin',
  scrollbarColor: '#CBD5E0 #F7FAFC',
})

export const ProgressBarWrap = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: '#fff',
  borderRadius: '30px',
  padding: '12px 20px',
  boxShadow: '0 1px 3px rgba(16, 24, 40, 0.06)',
})

export const ProgressTrack = styled('div')({
  height: 6,
  flex: 1,
  background: '#EDF1F5',
  borderRadius: 3,
  margin: '0 12px',
})

export const ProgressFill = styled('div')(({ pct }) => ({
  height: '100%',
  width: `${pct}%`,
  background: theme.talentinoBlue,
  borderRadius: 3,
  transition: 'width 0.3s ease',
}))

export const QuestionCard = styled(Card)({
  borderRadius: '12px',
})

export const QuestionNumber = styled('span')({
  minWidth: 28,
  height: 28,
  borderRadius: '50%',
  background: '#F1F5FA',
  color: theme.brandColorOrange,
  fontSize: '13px',
  fontWeight: 'bold',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 2,
})

export const ErrorText = styled('p')({
  fontSize: '12px',
  color: theme.talentinoDarkerRed,
})

export const MutedText = styled('p')({
  fontSize: '12px',
  color: '#666',
})

export const SubmitButton = styled(PrimaryButton)({
  borderRadius: '10px',
  fontSize: '16px',
})

/* ---------- inputs ---------- */

export const OptionLabel = styled('span')({
  fontSize: '14px',
})

export const NumberField = styled(Form.Control)({
  maxWidth: 200,
  fontSize: '13px',
})

export const RangeField = styled(Form.Control)({
  maxWidth: 120,
  fontSize: '13px',
})

export const RangeLabel = styled(Form.Label)({
  fontSize: '12px',
  color: '#6c757d',
})

export const TextArea = styled(Form.Control)({
  fontSize: '13px',
  resize: 'none',
})

/* ---------- attachment ---------- */

export const DropZone = styled('label')(({ dragActive, uploading }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  padding: '28px 16px',
  borderRadius: '12px',
  border: `1.5px dashed ${dragActive ? theme.talentinoBlue : '#C4D3E0'}`,
  background: dragActive ? '#EEF6FC' : theme.talentinoLightBackground,
  cursor: uploading ? 'not-allowed' : 'pointer',
  transition: 'all 0.2s ease',
  textAlign: 'center',
}))

export const HiddenFileInput = styled('input')({
  display: 'none',
})

export const DropIcon = styled('span')({
  width: 44,
  height: 44,
  borderRadius: '50%',
  background: theme.talentinoLightBlueBackground,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

export const UploadTrack = styled('div')({
  height: 4,
  background: '#e9ecef',
  borderRadius: 2,
})

export const UploadFill = styled('div')(({ pct }) => ({
  height: '100%',
  width: `${pct}%`,
  background: theme.talentinoBlue,
  borderRadius: 2,
  transition: 'width 0.2s ease',
}))

/* ---------- code editor ---------- */

export const CodeWrap = styled('div')({
  border: '1px solid #2a2a2a',
  borderRadius: 8,
  overflow: 'hidden',
  fontFamily: 'monospace',
  fontSize: 13,
})

export const CodeHeaderBar = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  background: '#252525',
  borderBottom: '1px solid #333',
})

export const CodeDots = styled('div')({
  display: 'flex',
  gap: 6,
})

export const CodeDot = styled('div')({
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: '#444',
})

export const CodeHeaderRight = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

export const LangBadge = styled('span')({
  fontSize: 11,
  color: '#888',
  padding: '2px 8px',
  border: '1px solid #333',
  borderRadius: 4,
})

export const CodeBody = styled('div')({
  display: 'flex',
  background: '#1e1e1e',
})

export const LineNums = styled('div')({
  padding: '12px 0',
  background: '#252525',
  borderRight: '1px solid #2a2a2a',
  minWidth: 40,
  textAlign: 'right',
  overflowY: 'hidden',
  userSelect: 'none',
})

export const LineNum = styled('span')({
  display: 'block',
  padding: '0 10px 0 8px',
  lineHeight: '20px',
  fontSize: 12,
  color: '#555',
})

export const CodeTextarea = styled('textarea')({
  flex: 1,
  border: 'none',
  outline: 'none',
  resize: 'none',
  padding: 12,
  lineHeight: '20px',
  fontFamily: 'monospace',
  fontSize: 13,
  background: 'transparent',
  color: '#d4d4d4',
  caretColor: '#569cd6',
  whiteSpace: 'pre',
})

export const CodeFooter = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '6px 12px',
  background: '#252525',
  borderTop: '1px solid #333',
})

export const CodeStat = styled('span')({
  fontSize: 11,
  color: '#555',
})
